import {
  Box,
  ToggleButton,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  alpha,
  styled,
  useTheme
} from '@mui/material';
import AccessGuard, { rules, useAccess } from 'access';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { MHidden } from '../../components/@material-extend';
import Logo from '../../components/util/Logo';
import NavSection from '../../components/util/NavSection';
import Scrollbar from '../../components/util/Scrollbar';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import { getSidebarConfig, SidebarItem } from './SidebarConfig';
import { PATH_DASHBOARD } from 'routes/paths';
import Icon from 'components/icons/Icon';
import { useTranslator } from 'translation/useTranslator';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import { FeatureFlagId } from '../../@types/featureFlags';

export const DRAWER_WIDTH = 280;
export const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

type IconCollapseProps = {
  onToggleCollapse: VoidFunction;
  collapseClick: boolean;
};

function IconCollapse({ onToggleCollapse, collapseClick }: IconCollapseProps) {
  const translator = useTranslator();
  return (
    <Box
      sx={{
        px: 2.5
      }}
    >
      <Tooltip title={translator.miniMenu()}>
        <ToggleButton
          value={!collapseClick}
          selected={!collapseClick}
          onClick={onToggleCollapse}
          size="small"
        >
          <Icon type="pin" isText />
        </ToggleButton>
      </Tooltip>
    </Box>
  );
}

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }: DashboardSidebarProps) {
  const translator = useTranslator();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isEnabledForCurrentOrganization } = useFeatureFlags();
  const canReadAiFeedback = useAccess(rules.aiFeedback.read);

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const conditionallyEnabledGeneralItems: SidebarItem[] =
    isEnabledForCurrentOrganization(FeatureFlagId.FeedbackAnalysis) && canReadAiFeedback
      ? [{ path: PATH_DASHBOARD.aiFeedback.root, title: 'AI', icon: <Icon type="ai" /> }]
      : [];
  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        overflowX: 'hidden'
      }}
    >
      <Stack
        sx={{
          pt: 1.5
        }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box component={RouterLink} to="/" style={{ width: COLLAPSE_WIDTH }}>
          <Logo sx={{ width: 30, marginInline: 'auto' }} />
        </Box>
        <div style={{ position: 'absolute', top: '1.5rem', right: 0 }}>
          <MHidden width="lgDown">
            {!isCollapse && (
              <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
            )}
          </MHidden>
        </div>
      </Stack>

      <NavSection
        navConfig={getSidebarConfig([], conditionallyEnabledGeneralItems, translator)}
        isShow={!isCollapse || (isMobile && isOpenSidebar)}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: 2 }} />

      <AccessGuard rule={rules.adminArea}>
        <div
          style={{
            padding: '1rem',
            width: COLLAPSE_WIDTH,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <IconButton
            sx={{ width: 'min-content' }}
            onClick={() => navigate(PATH_DASHBOARD.admin.root)}
          >
            <Icon type="settings" />
          </IconButton>
        </div>
      </AccessGuard>
    </Scrollbar>
  );

  return (
    <>
      {collapseClick && <div style={{ width: COLLAPSE_WIDTH }} />}
      <RootStyle
        sx={{
          width: {
            lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
          },
          ...(collapseClick && {
            position: 'absolute'
          })
        }}
      >
        <MHidden width="lgUp">
          <Drawer
            open={isOpenSidebar}
            onClose={onCloseSidebar}
            PaperProps={{
              sx: { width: DRAWER_WIDTH }
            }}
          >
            {renderContent}
          </Drawer>
        </MHidden>

        <MHidden width="lgDown">
          <Drawer
            open
            variant="persistent"
            onMouseEnter={onHoverEnter}
            onMouseLeave={onHoverLeave}
            PaperProps={{
              sx: {
                transition: 'width .2s',
                width: DRAWER_WIDTH,
                bgcolor: 'background.default',
                ...(isCollapse && {
                  width: COLLAPSE_WIDTH
                }),
                ...(collapseHover && {
                  borderRight: 0,
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                  boxShadow: (theme) => theme.customShadows.z20,
                  bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
                })
              }
            }}
          >
            {renderContent}
          </Drawer>
        </MHidden>
      </RootStyle>
    </>
  );
}
